import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, Plus } from 'lucide-react';
import { toast } from 'sonner';
import DashboardLayout from '../components/DashboardLayout';
import api from '../api';
import { TimeClockCard } from '../components/timeclock/TimeClockCard';
import { WeeklyTimesheet } from '../components/timeclock/WeeklyTimesheet';
import { EntryDialog } from '../components/timeclock/EntryDialog';
import { PayPeriodTable } from '../components/timeclock/PayPeriodTable';
import { ChangeRequests } from '../components/timeclock/ChangeRequests';
import { VisitTracker } from '../components/timeclock/VisitTracker';
import { VisitMap } from '../components/timeclock/VisitMap';
import { addDays, downloadCsv, fetchEntries, fmtDate, fmtTime, hrs, setAgencyTz, summarize, weekStart } from '../utils/timeclock';

const WeekView = ({ staffId, isOffice, staff, visitTypes, refreshKey }) => {
  const [start, setStart] = useState(weekStart());
  const [entries, setEntries] = useState([]);
  const [dlg, setDlg] = useState(null);
  const load = useCallback(() => fetchEntries(start, addDays(start, 7), staffId).then(setEntries).catch(() => toast.error('Could not load time entries')), [start, staffId]);
  useEffect(() => { load(); }, [load, refreshKey]);
  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(start, i)), [start]);
  const summary = summarize(entries, days);
  const name = staff.find((s) => s.id === staffId)?.name || 'my';
  const csv = () => downloadCsv(`timesheet_${name.replace(/\s+/g, '_')}_${fmtDate(start.toISOString()).replace(/\//g, '-')}.csv`,
    [['Date', 'Clock in', 'Clock out', 'Type', 'Patient', 'Hours', 'Miles', 'In location', 'Out location', 'Notes'],
      ...entries.map((e) => [fmtDate(e.clockIn), fmtTime(e.clockIn), e.clockOut ? fmtTime(e.clockOut) : 'missing', e.kind === 'visit' ? e.visitType : 'Shift', e.patientName || '',
        e.clockOut ? hrs((new Date(e.clockOut) - new Date(e.clockIn)) / 3600000) : '', e.mileage ?? '',
        e.inLocation?.lat != null ? `${e.inLocation.lat},${e.inLocation.lng}` : '', e.outLocation?.lat != null ? `${e.outLocation.lat},${e.outLocation.lng}` : '', e.notes || '']),
      ['Week total', '', '', '', '', hrs(summary.total.paid), summary.total.mileage.toFixed(1)], ['Regular', hrs(summary.total.reg)], ['Overtime', hrs(summary.total.ot)], ['Double time', hrs(summary.total.dt)]]);
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2 print:hidden">
        <button onClick={() => setStart(addDays(start, -7))} className="p-2 border rounded hover:bg-amber-50" data-testid="week-prev"><ChevronLeft className="w-4 h-4" /></button>
        <span className="text-sm font-semibold" data-testid="week-label">Week of {start.toLocaleDateString()} – {addDays(start, 6).toLocaleDateString()}</span>
        <button onClick={() => setStart(addDays(start, 7))} className="p-2 border rounded hover:bg-amber-50" data-testid="week-next"><ChevronRight className="w-4 h-4" /></button>
        <button onClick={() => setStart(weekStart())} className="px-2 py-1.5 border rounded text-sm" data-testid="week-today">This week</button>
        <button onClick={csv} className="px-2 py-1.5 border rounded text-sm hover:bg-amber-50" data-testid="week-csv">Download CSV</button>
        <button onClick={() => window.print()} className="px-2 py-1.5 border rounded text-sm hover:bg-amber-50" data-testid="week-print">Print</button>
        {isOffice && <button onClick={() => setDlg({ mode: 'add' })} className="inline-flex items-center gap-1 px-2 py-1.5 rounded bg-gray-900 text-[#d4af37] text-sm" data-testid="add-missed-entry"><Plus className="w-4 h-4" />Add missed entry</button>}
      </div>
      <WeeklyTimesheet summary={summary} editLabel={isOffice ? 'Fix' : 'Request change'} onEdit={(e) => setDlg({ mode: isOffice ? 'edit' : 'request', entry: e })} />
      {dlg && <EntryDialog open mode={dlg.mode} entry={dlg.entry} staff={staffId ? staff.filter((s) => s.id === staffId) : staff} visitTypes={visitTypes} onClose={() => setDlg(null)} onSaved={load} />}
    </div>
  );
};

const Tab = ({ id, cur, set, children }) => (
  <button onClick={() => set(id)} data-testid={`timeclock-tab-${id}`} className={`px-3 py-2 text-sm font-semibold border-b-2 ${cur === id ? 'border-[#d4af37] text-gray-900' : 'border-transparent text-gray-500 hover:text-gray-800'}`}>{children}</button>
);

export default function TimeClockPage() {
  const [st, setSt] = useState(null);
  const [staff, setStaff] = useState([]);
  const [tab, setTab] = useState('mine');
  const [who, setWho] = useState('');
  const [refresh, setRefresh] = useState(0);
  const me = JSON.parse(localStorage.getItem('ivory_user') || '{}');
  useEffect(() => {
    api.get('/timeclock/status').then((r) => {
      setAgencyTz(r.data.timezone);
      setSt(r.data);
      if (r.data.isOffice) api.get('/timeclock/staff').then((s) => { setStaff(s.data.staff); setWho(s.data.staff[0]?.id || ''); });
    }).catch(() => {});
  }, []);
  if (!st) return <DashboardLayout><p className="p-6 text-gray-500">Loading…</p></DashboardLayout>;
  return (
    <DashboardLayout>
      <div className="space-y-5 max-w-6xl" data-testid="time-clock-page">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Time Clock & Timesheets</h1>
          <p className="text-sm text-gray-600">Clock in and out for your shift and for each patient visit. Your location is saved with each punch.</p>
          {st.isOffice && (
            <p className="text-xs text-gray-500 mt-1" data-testid="timeclock-reminder-status">
              Visit reminders: email on · text messages {st.textsOn ? <span className="text-green-700 font-semibold">on</span> : <span className="text-amber-700 font-semibold">off until the RingCentral texting number and app keys are added</span>}
              {' '}· address lookup: {st.googleLookup ? 'Google Maps' : 'free OpenStreetMap'}
            </p>
          )}
        </div>
        <TimeClockCard onChange={() => setRefresh((n) => n + 1)} />
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
          <div className="flex flex-wrap gap-1 px-3 border-b print:hidden">
            <Tab id="mine" cur={tab} set={setTab}>My timesheet</Tab>
            {st.isOffice && <Tab id="tracker" cur={tab} set={setTab}>Visit tracker</Tab>}
            {st.isOffice && <Tab id="map" cur={tab} set={setTab}>Visit map</Tab>}
            {st.isOffice && <Tab id="employee" cur={tab} set={setTab}>Employee timesheets</Tab>}
            {st.isOffice && <Tab id="period" cur={tab} set={setTab}>Pay period – all employees</Tab>}
            <Tab id="requests" cur={tab} set={setTab}>{st.isOffice ? 'Change requests' : 'My change requests'}</Tab>
          </div>
          <div className="p-4">
            {tab === 'mine' && <WeekView staffId={me.id} isOffice={false} staff={[]} visitTypes={st.visitTypes} refreshKey={refresh} />}
            {tab === 'employee' && (
              <div className="space-y-3">
                <select value={who} onChange={(e) => setWho(e.target.value)} className="border rounded-lg px-2 py-2 text-sm print:hidden" data-testid="timesheet-employee-select">
                  {staff.map((s) => <option key={s.id} value={s.id}>{s.name} – {s.role}</option>)}
                </select>
                {who && <WeekView key={who} staffId={who} isOffice staff={staff} visitTypes={st.visitTypes} refreshKey={refresh} />}
              </div>
            )}
            {tab === 'tracker' && <VisitTracker />}
            {tab === 'map' && <VisitMap />}
            {tab === 'period' && <PayPeriodTable staff={staff} onOpen={(id) => { setWho(id); setTab('employee'); }} />}
            {tab === 'requests' && <ChangeRequests isOffice={st.isOffice} onDecided={() => setRefresh((n) => n + 1)} />}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
